import React, { useCallback, useEffect } from 'react';
import type { FC } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';
import { css } from '@compiled/react';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Flex, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import DropdownMenu, {
	DropdownItem,
	DropdownItemCheckbox,
	DropdownItemGroup,
	DropdownItemCheckboxGroup,
} from '@atlaskit/dropdown-menu';
import { IconButton } from '@atlaskit/button/new';
import FilterIcon from '@atlaskit/icon/core/filter';
import { AnnotationUpdateEvent } from '@atlaskit/editor-common/types';

import { CommentWarningDialog } from '@confluence/comment-dialogs';
import { useDialogs } from '@confluence/dialogs/entry-points/useDialogs';
import { fg } from '@confluence/feature-gating';
import { getRendererAnnotationEventEmitter } from '@confluence/annotation-event-emitter/entry-points/getRendererAnnotationEventEmitter';
import {
	useAnnotations,
	useAnnotationsDispatch,
} from '@confluence/annotation-provider-store/entry-points/AnnotationsContext';
import { useInlineCommentsContext } from '@confluence/comment-context/entry-points/InlineCommentsContext';
import {
	useCommentsContentState,
	useCommentsContentActions,
} from '@confluence/comment-context/entry-points/useCommentsContent';
import { useIsNewDanglingCommentExperienceEnabled } from '@confluence/comments-panel-utils/entry-points/useIsNewDanglingCommentExperienceEnabled';
import {
	useCommentsPanel,
	SortValues,
	useCommentsPanelActions,
	ViewValues,
	CommentMode,
} from '@confluence/comments-panel-utils/entry-points/useCommentsPanel';
import { AnalyticsSource } from '@confluence/comments-util/entry-points/analytics';
import { useContentType } from '@confluence/page-context';
import { PageMode } from '@confluence/page-utils/entry-points/enums';
import { useGetPageMode } from '@confluence/page-utils/entry-points/useGetPageMode';

import { getViewOptions, type ViewFilter } from './viewOptions';
import { CommentSortOptions } from './components/CommentSortOptions';
import { CommentBulkActions } from './components/CommentBulkActions';

const i18n = defineMessages({
	filterAndSortLabel: {
		id: 'comments-panel.filter.and.sort.label',
		defaultMessage: 'Filter and sort',
		description: 'Label for button apply filters and sorting to comment panel',
	},
	filterTitle: {
		id: 'comments-panel.filter.title',
		defaultMessage: 'Filter',
		description: 'A title for the filter section',
	},
	showTitle: {
		id: 'comments-panel.show.title',
		defaultMessage: 'Show',
		description: 'A title for show section',
	},
	inlineCommentsLabel: {
		id: 'comments-panel.inline-comments.label',
		defaultMessage: 'Inline comments',
		description: 'Label for the show inline comments dropdown option',
	},
	generalCommentsLabel: {
		id: 'comments-panel.general-comments.label',
		defaultMessage: 'General comments',
		description: 'Label for the show general comments dropdown option',
	},
});

const dropdownItemStyles = css({
	paddingLeft: `${token('space.200')}`,
});

const dropDownMenuStyles = xcss({
	display: 'flex',
	alignItems: 'center',
	paddingLeft: 'space.300',
	gap: 'space.100',
});

export const CommentsPanelFilterMenu: FC = () => {
	const { formatMessage } = useIntl();
	const { showModal } = useDialogs();
	const { hasContentChanged } = useCommentsContentState();
	const { resetContentChanged } = useCommentsContentActions();
	const pageMode = useGetPageMode();
	const eventEmitter = getRendererAnnotationEventEmitter();
	const {
		viewProps,
		editorViewProps,
		selectionProps,
		hoverProps,
		editorCreateProps,
		showCreateComponent,
		selectionOptions,
	} = useAnnotations();
	const { newCommentHighlight, activeHighlight } = useInlineCommentsContext();
	const {
		setViewProps,
		setEditorViewProps,
		setSelectionProps,
		setHoverProps,
		setEditorCreateProps,
		setShowCreateComponent,
		setSelectionOptions,
	} = useAnnotationsDispatch();
	const [
		{ currentView, showInlineComments, showGeneralComments, currentSort },
		{ setCurrentView, setShowInlineComments, setShowGeneralComments, setCurrentSort },
	] = useCommentsPanel();
	const { setCommentMode, setCurrentlySelectedThreadKey } = useCommentsPanelActions();
	const [contentType] = useContentType();
	const isDatabaseOrWhiteboard = ['database', 'whiteboard'].includes(contentType ?? 'page');
	const isNewDanglingCommentExperienceEnabled = useIsNewDanglingCommentExperienceEnabled();

	useEffect(() => {
		return () => {
			eventEmitter.emit('removehoveredannotation', '');
		};
	}, [eventEmitter]);

	useEffect(() => {
		// Set the sort to most recent for database and whiteboard pages
		if (isDatabaseOrWhiteboard && setCurrentSort) {
			setCurrentSort(SortValues.MOST_RECENT);
		}
	}, [contentType, isDatabaseOrWhiteboard, setCurrentSort]);

	const clearAnnotationSelections = useCallback(() => {
		// Clear selected comment thread in the comments panel
		setCurrentlySelectedThreadKey('');

		// Unselect active annotations on the page based on page mode (similar to onPanelCloseFunction)
		if (pageMode === PageMode.VIEW) {
			if (viewProps) {
				// Inform the Renderer to de-select the annotation
				eventEmitter.emit(AnnotationUpdateEvent.DESELECT_ANNOTATIONS);
				eventEmitter.emit(AnnotationUpdateEvent.REMOVE_ANNOTATION_FOCUS);
				// Remove the display of the comment
				setViewProps(null);
			}
		} else {
			if (editorViewProps) {
				// Inform the editor to de-select the annotation
				eventEmitter.emit('setselectedannotation', '');
				// Remove the display of the comment
				setEditorViewProps(null);
			}
		}

		if (showCreateComponent && selectionOptions) {
			// Inform CreateComment to reset the state
			setShowCreateComponent(false);
			setSelectionOptions(undefined);
		}

		if (selectionProps) {
			selectionProps.onClose();
			setSelectionProps(null);
		}

		if (hoverProps) {
			hoverProps.onClose();
			setHoverProps(null);
		}

		if (editorCreateProps) {
			editorCreateProps?.onClose && editorCreateProps.onClose();
			setEditorCreateProps(null);
		}

		// LEGACY PAGES
		if (newCommentHighlight || activeHighlight) {
			// Close the legacy inline comment panel
			window.dispatchEvent(new Event('closeLegacyInlineCommentSidebar'));
		}
	}, [
		pageMode,
		viewProps,
		editorViewProps,
		showCreateComponent,
		selectionOptions,
		selectionProps,
		hoverProps,
		editorCreateProps,
		newCommentHighlight,
		activeHighlight,
		eventEmitter,
		setCurrentlySelectedThreadKey,
		setViewProps,
		setEditorViewProps,
		setShowCreateComponent,
		setSelectionOptions,
		setSelectionProps,
		setHoverProps,
		setEditorCreateProps,
	]);

	const updateView = (view: ViewValues) => {
		const handleClick = () => {
			// Only clear annotation selections if the feature gate is enabled
			if (fg('confluence_comments_selection_fix')) {
				clearAnnotationSelections();
			}

			// Do nothing if the same view is clicked
			if (currentView !== view) {
				setCurrentView && setCurrentView(view);
			}
			// Set comment mode to VIEW when filter is changed
			setCommentMode(CommentMode.VIEW);
		};

		if (hasContentChanged) {
			showModal(CommentWarningDialog, {
				onConfirm: () => {
					resetContentChanged();
					handleClick();
				},
			});
		} else {
			handleClick();
		}
	};

	const toggleShow = (type: 'inline' | 'general') => {
		if (type === 'inline') {
			setShowInlineComments && setShowInlineComments(!showInlineComments);
		} else if (type === 'general') {
			setShowGeneralComments && setShowGeneralComments(!showGeneralComments);
		}
	};

	const defaultSortOrder =
		isDatabaseOrWhiteboard && fg('confluence_frontend_cp_variable_default_sort_order')
			? SortValues.MOST_RECENT
			: SortValues.PAGE_ORDER;

	const isDefaultFilterState =
		currentSort === defaultSortOrder &&
		showInlineComments &&
		showGeneralComments &&
		currentView === ViewValues.OPEN;

	const currentFilterIcon = isDefaultFilterState ? FilterIcon : CustomSelectedFilterIcon;

	const icon = fg('confluence_frontend_comments_filter_selected_state')
		? currentFilterIcon
		: FilterIcon;

	const viewOptions = getViewOptions(isNewDanglingCommentExperienceEnabled);

	return (
		<Flex role="tablist" xcss={dropDownMenuStyles}>
			<DropdownMenu
				trigger={({ triggerRef, ...props }) => (
					<IconButton
						testId="comments-panel-filter-button"
						ref={triggerRef}
						appearance="subtle"
						isTooltipDisabled={false}
						/**
						 * The IconButton suggests it can be given a selected state through the following
						 * `isSelected={showInlineComments && showGeneralComments && currentView !== 'OPEN'}`.
						 *
						 * From testing this does not currently work, so temporarily using a custom icon with
						 * the selected color set.
						 *
						 * Noting: This has accessibility issues (it's only providing the feedback to visual users)
						 * in addition to this the use of a button for a option selector is likely to have
						 * additional accessibility issues.
						 */
						icon={icon}
						label={formatMessage(i18n.filterAndSortLabel)}
						analyticsContext={{
							attributes: {
								name: 'filterButton',
								source: AnalyticsSource.COMMENTS_PANEL,
							},
						}}
						{...props}
					/>
				)}
				testId="filter-selector-dropdown"
				placement="bottom-end"
				spacing="compact"
				shouldRenderToParent
			>
				<DropdownItemGroup id="filter" title={formatMessage(i18n.filterTitle)}>
					{viewOptions.map((view: ViewFilter) => (
						<DropdownItem
							css={dropdownItemStyles}
							key={view.name}
							onClick={() => {
								// Add a small delay to prevent race condition with mouse events
								// when dropdown closes and mouse ends up over a comment thread
								setTimeout(() => {
									updateView(view.name);
								}, 0);
							}}
							data-testId={`menu-item-${view.name}-filter`}
							isSelected={view.name === currentView}
						>
							{formatMessage(view.label)}
						</DropdownItem>
					))}
				</DropdownItemGroup>
				{isDatabaseOrWhiteboard && fg('cc-whiteboards-consolidating-comments') ? null : (
					<CommentSortOptions />
				)}
				{isDatabaseOrWhiteboard && fg('cc-whiteboards-consolidating-comments') ? null : (
					<DropdownItemCheckboxGroup id="show" title={formatMessage(i18n.showTitle)} hasSeparator>
						{!fg('confluence_frontend_content_wrapper') && (
							<DropdownItemCheckbox
								onClick={() => toggleShow('inline')}
								id="toggle-inline-comments"
								data-testId="toggle-inline-comments"
								isSelected={showInlineComments}
							>
								{formatMessage(i18n.inlineCommentsLabel)}
							</DropdownItemCheckbox>
						)}
						<DropdownItemCheckbox
							onClick={() => toggleShow('general')}
							id="toggle-general-comments"
							data-testId="toggle-general-comments"
							isSelected={showGeneralComments}
						>
							{formatMessage(i18n.generalCommentsLabel)}
						</DropdownItemCheckbox>
					</DropdownItemCheckboxGroup>
				)}
				{fg('confluence_frontend_content_wrapper') && <CommentBulkActions />}
			</DropdownMenu>
		</Flex>
	);
};

function CustomSelectedFilterIcon(props: React.ComponentProps<typeof FilterIcon>) {
	return <FilterIcon {...props} color={token('color.icon.selected')} />;
}
